import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Wallet, 
  CurrencyDollar, 
  ArrowUp, 
  ArrowDown,
  Bank,
  Lightning,
  Clock,
  CalendarBlank,
  CheckCircle,
  XCircle,
  SpinnerGap,
  Warning,
  CaretRight,
  ShieldCheck,
  Crown,
  Question,
  Export,
  Receipt,
  Coins,
  TrendUp,
  Info
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { walletAPI } from '../api/api';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { BreadcrumbsInline } from '../components/Breadcrumbs';

const WalletPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Withdrawal modal state
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawSpeed, setWithdrawSpeed] = useState('standard');
  const [withdrawMethod, setWithdrawMethod] = useState('stripe');
  const [withdrawNotes, setWithdrawNotes] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  
  // Manual payout details
  const [bankName, setBankName] = useState('');
  const [accountLastFour, setAccountLastFour] = useState('');
  
  // Stripe Connect onboarding
  const [connectLoading, setConnectLoading] = useState(false);

  // Check for return from Stripe onboarding
  useEffect(() => {
    const stripeReturn = searchParams.get('stripe_return');
    if (stripeReturn === 'success') {
      toast({ title: 'Stripe Connected!', description: 'Your account is now linked for instant payouts.' });
      // Clear the URL param
      navigate('/wallet', { replace: true });
    } else if (stripeReturn === 'refresh') {
      toast({ title: 'Onboarding Incomplete', description: 'Please complete the Stripe onboarding process.', variant: 'destructive' });
      navigate('/wallet', { replace: true });
    }
  }, [searchParams, navigate]);

  // Fetch wallet data
  const fetchWalletData = useCallback(async () => {
    try {
      const response = await walletAPI.getBalance();
      if (response.data.success) {
        setWalletData(response.data);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
      toast({ title: 'Error', description: 'Failed to load wallet data', variant: 'destructive' });
    }
  }, []);

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    try {
      const response = await walletAPI.getTransactions({ limit: 20 });
      if (response.data.success) {
        setTransactions(response.data.transactions);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  }, []);

  // Fetch payouts
  const fetchPayouts = useCallback(async () => {
    try {
      const response = await walletAPI.getPayoutHistory(20);
      if (response.data.success) {
        setPayouts(response.data.payouts);
      }
    } catch (error) {
      console.error('Error fetching payouts:', error);
    }
  }, []);

  // Fetch Stripe status
  const fetchStripeStatus = useCallback(async () => {
    try {
      const response = await walletAPI.getStripeStatus();
      if (response.data.success) {
        setStripeStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    if (userType !== 'worker' && userType !== 'contractor') {
      // Send homeowners back to their dashboard
      navigate(userType === 'homeowner' ? '/homeowner-dashboard' : '/');
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchWalletData(),
        fetchTransactions(),
        fetchPayouts(),
        fetchStripeStatus()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchWalletData, fetchTransactions, fetchPayouts, fetchStripeStatus, navigate]);

  // Start Stripe Connect onboarding
  const handleStripeConnect = async () => {
    setConnectLoading(true);
    try {
      const baseUrl = window.location.origin;
      const response = await walletAPI.startStripeOnboarding(
        `${baseUrl}/wallet?stripe_return=success`,
        `${baseUrl}/wallet?stripe_return=refresh`
      );
      
      if (response.data.success && response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast({ 
        title: 'Connection Failed', 
        description: error.response?.data?.detail || 'Could not start Stripe onboarding', 
        variant: 'destructive' 
      });
    } finally {
      setConnectLoading(false);
    }
  };

  // Submit withdrawal
  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < (walletData?.wallet?.minimum_payout || 25)) {
      toast({ 
        title: 'Invalid Amount', 
        description: `Minimum withdrawal is $${walletData?.wallet?.minimum_payout || 25}`, 
        variant: 'destructive' 
      });
      return;
    }
    
    if (amount > (walletData?.wallet?.available_balance || 0)) {
      toast({ title: 'Insufficient Balance', description: 'You cannot withdraw more than your available balance', variant: 'destructive' });
      return;
    }
    
    setWithdrawLoading(true);
    try {
      let response;
      if (withdrawMethod === 'stripe') {
        response = await walletAPI.requestStripePayout(amount, withdrawSpeed, withdrawNotes);
      } else {
        response = await walletAPI.requestManualPayout(amount, withdrawSpeed, {
          payout_method: 'bank_transfer',
          bank_name: bankName,
          account_last_four: accountLastFour,
          notes: withdrawNotes
        });
      }
      
      if (response.data.success) {
        toast({ title: 'Withdrawal Requested!', description: response.data.message });
        setShowWithdrawModal(false);
        resetWithdrawForm();
        // Refresh data
        fetchWalletData();
        fetchTransactions();
        fetchPayouts();
      }
    } catch (error) {
      toast({ 
        title: 'Withdrawal Failed', 
        description: error.response?.data?.detail || 'Could not process withdrawal', 
        variant: 'destructive' 
      });
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Reset form
  const resetWithdrawForm = () => {
    setWithdrawAmount('');
    setWithdrawSpeed('standard');
    setWithdrawMethod('stripe');
    setWithdrawNotes('');
    setBankName('');
    setAccountLastFour('');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      processing: 'bg-blue-100 text-blue-700 border-blue-300',
      failed: 'bg-red-100 text-red-700 border-red-300',
      rejected: 'bg-red-100 text-red-700 border-red-300'
    };
    return styles[status] || 'bg-neutral-100 text-neutral-700 border-neutral-300';
  };

  // Get transaction icon
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'escrow_release':
        return <ArrowDown size={16} weight="bold" className="text-emerald-500" />;
      case 'payout':
      case 'payout_hold':
        return <ArrowUp size={16} weight="bold" className="text-red-500" />;
      case 'payout_refund':
        return <ArrowDown size={16} weight="bold" className="text-blue-500" />;
      default:
        return <Coins size={16} weight="bold" className="text-neutral-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: '/worker-dashboard' },
              { label: 'Wallet' }
            ]}
            className="mb-3"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
                <Wallet size={32} weight="bold" />
              </div>
              <div>
                <h1 className="text-3xl font-black">My Wallet</h1>
                <p className="text-white/80">Manage your earnings and withdrawals</p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowWithdrawModal(true)}
              disabled={!walletData?.wallet?.available_balance || walletData.wallet.available_balance < (walletData?.wallet?.minimum_payout || 25)}
              className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-6 h-12 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none"
              data-testid="withdraw-btn"
            >
              <CurrencyDollar size={20} weight="bold" className="mr-2" />
              Withdraw Funds
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Balance Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          {/* Available Balance */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Available Balance</span>
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                <CurrencyDollar size={20} weight="bold" className="text-emerald-600" />
              </div>
            </div>
            <p className="text-4xl font-black text-neutral-900 dark:text-white">
              {formatCurrency(walletData?.wallet?.available_balance)}
            </p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              Ready to withdraw
            </p>
          </div>

          {/* Pending Balance */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Pending</span>
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <Clock size={20} weight="bold" className="text-amber-600" />
              </div>
            </div>
            <p className="text-4xl font-black text-amber-600">
              {formatCurrency(walletData?.wallet?.pending_balance)}
            </p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              In withdrawal process
            </p>
          </div>

          {/* Total Earned */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Total Earned</span>
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <TrendUp size={20} weight="bold" className="text-blue-600" />
              </div>
            </div>
            <p className="text-4xl font-black text-blue-600">
              {formatCurrency(walletData?.wallet?.total_earned)}
            </p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              All time earnings
            </p>
          </div>
        </div>

        {/* Elite fee waiver: perk badge or upsell */}
        {walletData?.wallet?.fee_waived && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border-2 border-emerald-300 dark:border-emerald-700 flex items-center gap-3" data-testid="elite-fee-waived-banner">
            <Crown size={24} weight="fill" className="text-amber-500" />
            <div>
              <p className="font-bold text-emerald-700 dark:text-emerald-400">Elite perk active — 0% platform fee</p>
              <p className="text-sm text-emerald-600 dark:text-emerald-500">The {walletData.wallet.base_platform_fee_percentage}% platform fee is waived on all your payouts. You keep 100%.</p>
            </div>
          </div>
        )}
        {!walletData?.wallet?.fee_waived && (walletData?.wallet?.base_platform_fee_percentage || 0) > 0 && localStorage.getItem('userType') === 'worker' && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white border-2 border-black shadow-[4px_4px_0px_#000]" data-testid="elite-fee-upsell-banner">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                  <Crown size={28} weight="fill" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Elite workers pay 0% platform fee</h3>
                  <p className="text-white/90 text-sm">
                    Upgrade to Elite and the {walletData.wallet.base_platform_fee_percentage}% payout fee disappears — keep 100% of every withdrawal.
                    {(walletData?.wallet?.available_balance || 0) > 0 && (
                      <> Withdrawing your {formatCurrency(walletData.wallet.available_balance)} balance today would save you <strong>{formatCurrency(walletData.wallet.available_balance * walletData.wallet.base_platform_fee_percentage / 100)}</strong>.</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-white text-orange-600 hover:bg-orange-50 font-bold px-6 border-2 border-black shadow-[2px_2px_0px_#000]"
                data-testid="elite-upsell-cta"
              >
                <Crown size={18} weight="fill" className="mr-2" />
                Go Elite
              </Button>
            </div>
          </div>
        )}

        {/* Stripe Connect Status */}
        {!walletData?.stripe_connected && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white border-2 border-black shadow-[4px_4px_0px_#000]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Lightning size={28} weight="fill" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Enable Instant Payouts</h3>
                  <p className="text-white/80">Connect your bank account via Stripe for instant withdrawals</p>
                </div>
              </div>
              <Button
                onClick={handleStripeConnect}
                disabled={connectLoading}
                className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-6 border-2 border-black shadow-[2px_2px_0px_#000]"
                data-testid="connect-stripe-btn"
              >
                {connectLoading ? (
                  <SpinnerGap size={20} className="animate-spin" />
                ) : (
                  <>
                    <Bank size={20} weight="bold" className="mr-2" />
                    Connect Bank
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {walletData?.stripe_connected && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border-2 border-emerald-300 dark:border-emerald-700 flex items-center gap-3">
            <CheckCircle size={24} weight="fill" className="text-emerald-500" />
            <div>
              <p className="font-bold text-emerald-700 dark:text-emerald-400">Stripe Connected</p>
              <p className="text-sm text-emerald-600 dark:text-emerald-500">Instant payouts available</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b-2 border-neutral-200 dark:border-neutral-700">
          {['overview', 'transactions', 'payouts'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 font-bold capitalize transition-colors ${
                activeTab === tab 
                  ? 'text-[#2B4C6F] border-b-4 border-[#2B4C6F] -mb-0.5' 
                  : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Recent Transactions */}
            <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden">
              <div className="p-4 bg-neutral-50 dark:bg-neutral-700 border-b-2 border-black dark:border-neutral-600 flex items-center justify-between">
                <h3 className="font-bold text-lg text-neutral-900 dark:text-white">Recent Transactions</h3>
                <button 
                  onClick={() => setActiveTab('transactions')}
                  className="text-sm text-[#2B4C6F] font-bold flex items-center gap-1"
                >
                  View All <CaretRight size={16} weight="bold" />
                </button>
              </div>
              
              <div className="divide-y-2 divide-neutral-100 dark:divide-neutral-700">
                {transactions.length === 0 ? (
                  <div className="p-8 text-center text-neutral-500">
                    <Receipt size={48} weight="light" className="mx-auto text-neutral-300 mb-2" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  transactions.slice(0, 5).map(tx => (
                    <div key={tx._id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-700 rounded-lg flex items-center justify-center">
                          {getTransactionIcon(tx.type)}
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">{tx.description}</p>
                          <p className="text-sm text-neutral-500">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <p className={`font-bold text-lg ${tx.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden">
            <div className="p-4 bg-neutral-50 dark:bg-neutral-700 border-b-2 border-black dark:border-neutral-600">
              <h3 className="font-bold text-lg text-neutral-900 dark:text-white">Transaction History</h3>
            </div>
            
            <div className="divide-y-2 divide-neutral-100 dark:divide-neutral-700">
              {transactions.length === 0 ? (
                <div className="p-12 text-center text-neutral-500">
                  <Receipt size={64} weight="light" className="mx-auto text-neutral-300 mb-4" />
                  <p className="text-lg font-medium">No transactions yet</p>
                  <p className="text-sm">Transactions will appear here when you receive payments</p>
                </div>
              ) : (
                transactions.map(tx => (
                  <div key={tx._id} className="p-4 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-750">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-700 rounded-lg flex items-center justify-center">
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">{tx.description}</p>
                        <p className="text-sm text-neutral-500">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${tx.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                      </p>
                      <p className="text-sm text-neutral-500">Balance: {formatCurrency(tx.balance_after)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'payouts' && (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden">
            <div className="p-4 bg-neutral-50 dark:bg-neutral-700 border-b-2 border-black dark:border-neutral-600">
              <h3 className="font-bold text-lg text-neutral-900 dark:text-white">Payout History</h3>
            </div>
            
            <div className="divide-y-2 divide-neutral-100 dark:divide-neutral-700">
              {payouts.length === 0 ? (
                <div className="p-12 text-center text-neutral-500">
                  <Export size={64} weight="light" className="mx-auto text-neutral-300 mb-4" />
                  <p className="text-lg font-medium">No payouts yet</p>
                  <p className="text-sm">Your withdrawal history will appear here</p>
                </div>
              ) : (
                payouts.map(payout => (
                  <div key={payout._id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        payout.payout_method === 'stripe' ? 'bg-indigo-100' : 'bg-neutral-100'
                      }`}>
                        {payout.payout_method === 'stripe' ? (
                          <Lightning size={20} weight="bold" className="text-indigo-600" />
                        ) : (
                          <Bank size={20} weight="bold" className="text-neutral-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">
                          {payout.payout_method === 'stripe' ? 'Stripe' : 'Manual'} Payout
                        </p>
                        <p className="text-sm text-neutral-500">
                          {formatDate(payout.created_at)} • {payout.payout_speed}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-neutral-900 dark:text-white">
                        {formatCurrency(payout.amount)}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full border font-medium ${getStatusBadge(payout.status)}`}>
                        {payout.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Withdraw Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-neutral-800 border-2 border-black shadow-[4px_4px_0px_#000]">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <CurrencyDollar size={24} weight="bold" className="text-emerald-500" />
              Withdraw Funds
            </DialogTitle>
            <DialogDescription>
              Available: {formatCurrency(walletData?.wallet?.available_balance)} • Minimum: {formatCurrency(walletData?.wallet?.minimum_payout || 25)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Amount */}
            <div>
              <Label className="font-bold">Withdrawal Amount</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-bold">$</span>
                <Input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.00"
                  min={walletData?.wallet?.minimum_payout || 25}
                  max={walletData?.wallet?.available_balance || 0}
                  className="pl-8 h-12 border-2 text-lg font-bold"
                />
              </div>
              <button 
                onClick={() => setWithdrawAmount(walletData?.wallet?.available_balance?.toString() || '0')}
                className="text-sm text-[#2B4C6F] font-bold mt-1"
              >
                Withdraw Max
              </button>
            </div>
            
            {/* Method */}
            <div>
              <Label className="font-bold">Withdrawal Method</Label>
              <Select value={withdrawMethod} onValueChange={setWithdrawMethod}>
                <SelectTrigger className="mt-1 h-12 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stripe" disabled={!walletData?.stripe_connected}>
                    <div className="flex items-center gap-2">
                      <Lightning size={16} weight="bold" className="text-indigo-600" />
                      Stripe (Instant Available)
                      {!walletData?.stripe_connected && <span className="text-red-500 text-xs ml-2">Not Connected</span>}
                    </div>
                  </SelectItem>
                  <SelectItem value="manual">
                    <div className="flex items-center gap-2">
                      <Bank size={16} weight="bold" />
                      Manual / Admin Approval
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Speed */}
            <div>
              <Label className="font-bold">Payout Speed</Label>
              <Select value={withdrawSpeed} onValueChange={setWithdrawSpeed}>
                <SelectTrigger className="mt-1 h-12 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant" disabled={withdrawMethod === 'manual'}>
                    <div className="flex items-center justify-between w-full">
                      <span>Instant (Minutes)</span>
                      <span className="text-amber-600 text-sm ml-4">+${walletData?.wallet?.instant_payout_fee || 1.50} fee</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="standard">
                    <span>Standard (2-3 Business Days)</span>
                  </SelectItem>
                  <SelectItem value="weekly">
                    <span>Weekly Batch</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Manual payout details */}
            {withdrawMethod === 'manual' && (
              <div className="space-y-3 p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
                <div className="flex items-start gap-2 mb-3">
                  <Info size={18} weight="bold" className="text-amber-500 mt-0.5" />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Manual payouts require admin approval and may take longer to process.
                  </p>
                </div>
                <div>
                  <Label className="text-sm">Bank Name (Optional)</Label>
                  <Input
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="e.g., Chase, Bank of America"
                    className="mt-1 border-2"
                  />
                </div>
                <div>
                  <Label className="text-sm">Last 4 of Account # (Optional)</Label>
                  <Input
                    value={accountLastFour}
                    onChange={(e) => setAccountLastFour(e.target.value)}
                    placeholder="1234"
                    maxLength={4}
                    className="mt-1 border-2"
                  />
                </div>
              </div>
            )}
            
            {/* Notes */}
            <div>
              <Label className="font-bold">Notes (Optional)</Label>
              <Textarea
                value={withdrawNotes}
                onChange={(e) => setWithdrawNotes(e.target.value)}
                placeholder="Any notes for this withdrawal..."
                className="mt-1 border-2"
              />
            </div>
            
            {/* Fee Summary */}
            {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border-2 border-emerald-200 dark:border-emerald-800">
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-600 dark:text-neutral-400">Withdrawal Amount</span>
                  <span className="font-bold">{formatCurrency(parseFloat(withdrawAmount))}</span>
                </div>
                {withdrawSpeed === 'instant' && withdrawMethod === 'stripe' && (
                  <div className="flex justify-between mb-2">
                    <span className="text-neutral-600 dark:text-neutral-400">Instant Fee</span>
                    <span className="font-bold text-amber-600">-{formatCurrency(walletData?.wallet?.instant_payout_fee || 1.50)}</span>
                  </div>
                )}
                {(walletData?.wallet?.platform_fee_percentage || 0) > 0 && (
                  <div className="flex justify-between mb-2" data-testid="platform-fee-row">
                    <span className="text-neutral-600 dark:text-neutral-400">Platform Fee ({walletData.wallet.platform_fee_percentage}%)</span>
                    <span className="font-bold text-amber-600">-{formatCurrency(parseFloat(withdrawAmount) * walletData.wallet.platform_fee_percentage / 100)}</span>
                  </div>
                )}
                {walletData?.wallet?.fee_waived && (walletData?.wallet?.base_platform_fee_percentage || 0) > 0 && (
                  <div className="flex justify-between mb-2" data-testid="platform-fee-waived-row">
                    <span className="text-emerald-600 flex items-center gap-1"><Crown size={13} weight="fill" className="text-amber-500" /> Platform Fee (waived — Elite)</span>
                    <span className="font-bold text-emerald-600">-{formatCurrency(0)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-emerald-300 dark:border-emerald-700">
                  <span className="font-bold text-emerald-700 dark:text-emerald-400">You'll Receive</span>
                  <span className="font-black text-emerald-700 dark:text-emerald-400">
                    {formatCurrency(
                      parseFloat(withdrawAmount) - 
                      (withdrawSpeed === 'instant' && withdrawMethod === 'stripe' ? (walletData?.wallet?.instant_payout_fee || 1.50) : 0) -
                      (parseFloat(withdrawAmount) * (walletData?.wallet?.platform_fee_percentage || 0) / 100)
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowWithdrawModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleWithdraw}
              disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) < (walletData?.wallet?.minimum_payout || 25)}
              className="bg-emerald-500 hover:bg-emerald-600 border-2 border-black shadow-[2px_2px_0px_#000]"
            >
              {withdrawLoading ? (
                <SpinnerGap size={20} className="animate-spin" />
              ) : (
                <>
                  <CheckCircle size={20} weight="bold" className="mr-2" />
                  Confirm Withdrawal
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletPage;
